import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import type { DayCardPreview } from '../../api/types'
import { useEscape } from '../../hooks/useEscape'
import { ausFeld, zuFeld, type Versatz } from '../../lib/ortszeit'
import { useTexte } from '../../texte'

/**
 * "Welcher Tag?" -- the question before a day card goes out (#79, 0.41.0).
 *
 * The server proposes the last day of the route that has points, today
 * included: whoever loads the recording in the evening and shares at once
 * gets today, whoever shares the next morning gets yesterday. The other
 * days of the route stand in the list, newest first, so that an older day
 * can be handed in later. A day without points is not in the list.
 *
 * **And since 0.42.0 "Von" and "Bis"** -- from use: "vielleicht will ich mal
 * nur einen Teil übernehmen." Both are prefilled with the first and the
 * last motion the server found for the day, in the trip's clock; whoever
 * wants a part of the day changes them. Choosing another day asks the
 * server for that day's motion and fills the fields again.
 *
 * Built like `Rueckfrage`: on `document.body`, Escape and a tap on the
 * background say no, the safe way has the focus.
 */
export function Tagwahl({
  vorschau,
  versatz,
  holen,
  onWahl,
  onAbbruch,
}: {
  vorschau: DayCardPreview
  versatz: Versatz
  holen: (day: string) => Promise<DayCardPreview>
  onWahl: (wahl: { day: string; from?: string; to?: string }) => void
  onAbbruch: () => void
}) {
  const t = useTexte()
  const [tag, setTag] = useState(vorschau.day)
  const [start, setStart] = useState(zeit(vorschau.stats.started_at, versatz))
  const [ende, setEnde] = useState(zeit(vorschau.stats.ended_at, versatz))
  const [laedt, setLaedt] = useState(false)
  useEscape(onAbbruch)

  // Another day: its first and last motion into the fields. The answer of
  // a day that is no longer chosen is dropped.
  useEffect(() => {
    if (tag === vorschau.day) {
      setStart(zeit(vorschau.stats.started_at, versatz))
      setEnde(zeit(vorschau.stats.ended_at, versatz))
      return
    }
    let gilt = true
    setLaedt(true)
    holen(tag)
      .then((p) => {
        if (!gilt) return
        setStart(zeit(p.stats.started_at, versatz))
        setEnde(zeit(p.stats.ended_at, versatz))
      })
      .catch(() => {
        if (gilt) {
          setStart('')
          setEnde('')
        }
      })
      .finally(() => {
        if (gilt) setLaedt(false)
      })
    return () => {
      gilt = false
    }
  }, [tag, vorschau, versatz, holen])

  function senden() {
    onWahl({
      day: tag,
      from: start === '' ? undefined : ausFeld(`${tag}T${start}`, versatz),
      to: ende === '' ? undefined : ausFeld(`${tag}T${ende}`, versatz),
    })
  }

  return createPortal(
    <div className="rueckfrage" role="dialog" aria-modal="true" aria-label={t.chat.tagWelcher} onClick={onAbbruch} data-tagwahl="">
      <div className="rueckfrage__blatt" onClick={(event) => event.stopPropagation()}>
        <p className="rueckfrage__frage">{t.chat.tagWelcher}</p>
        <label className="field field--dicht">
          <span className="field__label">{t.chat.tagFeld}</span>
          <select className="field__input" value={tag} onChange={(event) => setTag(event.target.value)} data-tagwahl-tag="">
            {vorschau.days.map((d) => (
              <option key={d.day} value={d.day}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
        <div className="tagwahl__zeiten">
          <label className="field field--dicht">
            <span className="field__label">{t.chat.tagVon}</span>
            <input className="field__input" type="time" value={start} onChange={(event) => setStart(event.target.value)} disabled={laedt} data-tagwahl-ab="" />
          </label>
          <label className="field field--dicht">
            <span className="field__label">{t.chat.tagBis}</span>
            <input className="field__input" type="time" value={ende} onChange={(event) => setEnde(event.target.value)} disabled={laedt} data-tagwahl-bis="" />
          </label>
        </div>
        <p className="leise">{t.chat.tagTeilHinweis}</p>
        <div className="rueckfrage__knoepfe">
          <button type="button" className="button button--outline button--small" onClick={onAbbruch} autoFocus>
            {t.allgemein.abbrechen}
          </button>
          <button type="button" className="button button--small" onClick={senden} disabled={laedt} data-tagwahl-senden="">
            {t.chat.tagSenden}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/** "08:12" in the trip's clock for a `<input type="time">`, or empty. */
function zeit(iso: string | null, versatz: Versatz): string {
  if (iso === null) return ''
  return zuFeld(iso, versatz).slice(11, 16)
}
